<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue';
import { fetchTags, createListing, updateListing, fetchListingById } from '@/services/api';
import { useLayoutComponents } from '@/composables/layout.js';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from '@/composables/use-toast';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import { useStorage } from '@/composables/use-storage';
import Multiselect from '@/components/forms/inputs/multiselect.vue';
import Toast from '@/components/toast.vue';
import Checkbox from '@/components/checkbox.vue';
import Alert from '@/components/Alert.vue';
import MarkdownRenderer from '@/components/MarkdownRenderer.vue';
import axios from 'axios';
import ListingLicenseModal from '@/pages/listing/listingLicenseModal.vue';
import ShowListingLicenseModal from '@/pages/listing/showListingLicenseModal.vue';

const { addToast } = useToast();

const { TextInput, InputError, ButtonLoading, SelectInput } = useLayoutComponents();

const route = useRoute();
const router = useRouter();

const { userInfo } = useStorage();

const imageUrl = ref('');

const listingId = computed(() => {
    return route.name === 'rfp-edit' ? route.params.id : null;
});

const title = ref('');
const modality = ref('');
const domain = ref('');
const useCase = ref('');
const description = ref('');
const shortDescription = ref('');
const price = ref('');
const license = ref('psdla');
const tags = ref([]);
const deliveryMethod = ref('dataset');
const previewImage = ref(null);


const isUploading = ref(false);
const uploadProgress = ref(0);
const hasUploadError = ref(false);
const isSubmitting = ref(false);
const isLoading = ref(true);

const errors = ref({
    title: '',
    modality: '',
    useCase: '',
    description: '',
    shortDescription: '',
    price: '',
    license: '',
    domain: '',
    deliveryMethod: '',
    previewImage: '',
    expiration: ''
});

const modalityOptions = ref([]);
const useCaseOptions = ref([]);

const licenseOptions = [
    { label: 'PSDLA', value: 'psdla' },
];

const domainOptions = ref([]);
const tagsLoaded = ref(false);

const showAlert = ref(false);
const showEditAlert = ref(false);
const alertTitle = ref('');
const alertMessage = ref('');
const alertType = ref('info');
const alertShowPasswordInput = ref(false);
const listingDataToSubmit = ref(null);

const showCropper = ref(false);
const imageSource = ref('');
const cropperResult = ref(null);
const cropperRef = ref(null);
const showListingForm = ref(true);


const isFileProcessing = ref(false);
const fileProcessingInterval = ref(null);

const doesNotExpire = ref(false);
const expirationDate = ref('');

const licenseData = ref(null);
const showLicenseModal = ref(false);
const showPreviewLicenseModal = ref(false);

onMounted(async () => {
    try {
        isLoading.value = true;
       
       
            await Promise.all([loadTags()]);
            if (listingId.value) {
                await loadListingData(listingId.value);
            }
            
    } catch (error) {
        console.error('Error during component initialization:', error);
    } finally {
        isLoading.value = false;
    }
});

onUnmounted(() => {
    if (route.name === 'rfp-edit') {
        localStorage.removeItem('temp_editing_listing_id');
    }
    if (fileProcessingInterval.value) {
        clearInterval(fileProcessingInterval.value);
    }
});

const loadTags = async () => {
    if (tagsLoaded.value) {
        return;
    }
    const tagsFromApi = await fetchTags();

    if (Array.isArray(tagsFromApi)) {
        const domainTags = tagsFromApi.map(tag => {
            if (typeof tag === 'string') {
                return { label: tag, value: tag.toLowerCase() };
            } else if (tag && typeof tag === 'object') {
                const tagType = tag.tag_type?.type?.toLowerCase();
                const isDomain = tagType === 'domain';

                const { label, value } = {
                    label: isDomain ? tag.name : '',
                    value: isDomain ? (
                        String(tag.id) || 
                        tag.tag_type?.value || 
                        tag.tag_type?.name?.toLowerCase() || 
                        ''
                    ) : ''
                };
                
                if (label !== '' && value !== '') {
                    return { label, value };
                }
            }
            return null;
        }).filter(Boolean);

        const modalityTags = tagsFromApi.map(tag => {
            if (typeof tag === 'string') {
                return { label: tag, value: tag.toLowerCase() };
            } else if (tag && typeof tag === 'object') {
                const tagType = tag.tag_type?.type?.toLowerCase();
                const isModality = tagType === 'modality';

                const { label, value } = {
                    label: isModality ? tag.name : '',
                    value: isModality ? (
                        String(tag.id) || 
                        tag.tag_type?.value || 
                        tag.tag_type?.name?.toLowerCase() || 
                        ''
                    ) : ''
                };
                
                if (label !== '' && value !== '') {
                    return { label, value };
                }
            }
            return null;
        }).filter(Boolean);

        const useCaseTags = tagsFromApi.map(tag => {
            if (typeof tag === 'string') {
                return { label: tag, value: tag.toLowerCase() };
            } else if (tag && typeof tag === 'object') {
                const tagType = tag.tag_type?.type?.toLowerCase();
                const isNotModalityOrDomain = tagType && tagType !== 'modality' && tagType !== 'domain';

                const { label, value } = {
                    label: isNotModalityOrDomain ? tag.name : '',
                    value: isNotModalityOrDomain ? (
                        String(tag.id) || 
                        tag.tag_type?.value || 
                        tag.tag_type?.name?.toLowerCase() || 
                        ''
                    ) : ''
                };
                
                if (label !== '' && value !== '') {
                    return { label, value };
                }
            }
            return null;
        }).filter(Boolean);

        modalityOptions.value = modalityTags;
        useCaseOptions.value = useCaseTags;
        domainOptions.value = domainTags;
        tagsLoaded.value = true;

    } else {
        domainOptions.value = [];
        modalityOptions.value = [];
    }
};

const removeSelectedImage = () => {
    previewImage.value = null;
    uploadProgress.value = 0;
    hasUploadError.value = false;
    errors.value.previewImage = '';

    const fileInput = document.getElementById('imageInput');
    if (fileInput) {
        fileInput.value = '';
    }
};

const generatePreviewImage = () => {
    console.log('Coming soon!');
};

const validateBasicFields = () => {
    const missingFields = [];
    
    if (!title.value) {
        errors.value.title = 'Title is required';
        missingFields.push('Title');
    }

    if (!modality.value || (Array.isArray(modality.value) && modality.value.length === 0)) {
        errors.value.modality = 'Modality is required';
        missingFields.push('Modality');
    }

    if (!description.value) {
        errors.value.description = 'Description is required';
        missingFields.push('Description');
    }

    if (!shortDescription.value) {
        errors.value.shortDescription = 'Short description is required';
        missingFields.push('Short Description');
    } else if (shortDescription.value.length > 300) {
        errors.value.shortDescription = 'Short description must not exceed 300 characters';
        missingFields.push('Short Description');
    }

    return missingFields;
};

const validatePriceFields = () => {
    const missingFields = [];

    if (!price.value) {
        errors.value.price = 'Price is required';
        missingFields.push('Price');
    }

    return missingFields;
};

const validateAdditionalFields = () => {
    const missingFields = [];

    if (!license.value) {
        errors.value.license = 'License is required';
        missingFields.push('License');
    }

    if (!previewImage.value || !previewImage.value.base64) {
        errors.value.previewImage = 'Preview image is required';
        missingFields.push('Preview Image');
    }

    if (!licenseData.value) {
        errors.value.license = 'License is required';
        missingFields.push('License');
    }

    return missingFields;
};

const validateForm = () => {
    errors.value = {
        title: '',
        modality: '',
        useCase: '',
        description: '',
        shortDescription: '',
        schema: '',
        price: '',
        license: '',
        domain: '',
        deliveryMethod: '',
        previewImage: ''
    };

    const missingFields = [
        ...validateBasicFields(),
        ...validatePriceFields(),
        ...validateAdditionalFields()
    ];

    if (missingFields.length > 0) {
        addToast(`Please fill in the following fields: ${missingFields.join(', ')}`, 'error');
    }

    return missingFields.length === 0;
};

const loadListingData = async (id) => {
    let listingData = await fetchListingById(id);
    if (listingData) {
        setBasicListingInfo(listingData);
        await loadAndSetTags(listingData);
        setPreviewImage(listingData);
        setListingLicenseInfo(listingData);
    }
};

const setBasicListingInfo = (listingData) => {
    title.value = listingData.name || '';
    description.value = listingData.description || '';
    shortDescription.value = listingData.short_description || '';
    tags.value = listingData.tags.map(tag => String(tag.id));
    price.value = listingData.price ? String(listingData.price) : '';
    license.value = listingData.data_license || '';
    deliveryMethod.value = listingData.delivery_method || '';
};

const setListingLicenseInfo = (listingData) => {
    if (listingData.listingLicenses && listingData.listingLicenses.length > 0) {
        licenseData.value = {
            licenseId: listingData.listingLicenses[0].license.id,
            licenseName: listingData.listingLicenses[0].license.name,
            customClauses: listingData.listingLicenses[0].custom_clauses,
            yearsDuration: listingData.listingLicenses[0].years_duration,
            allowedAdditions: listingData.listingLicenses[0].license.allow_additions
        };
    }
};

const loadAndSetTags = async (listingData) => {
    if (!tagsLoaded.value) {
        await loadTags();
    }

    tags.value = [];
    domain.value = [];
    modality.value = [];
    useCase.value = [];

    if (listingData.tags && Array.isArray(listingData.tags)) {
        tags.value = listingData.tags.map(tag => String(tag.id));
    } else if (listingData.listing_tags && Array.isArray(listingData.listing_tags)) {
        tags.value = listingData.listing_tags.map(tag =>
            (tag.tag_id !== undefined) ? String(tag.tag_id) : String(tag.id)
        );
    }
    const domainTagIds = domainOptions.value.map(option => option.value);
    const modalityTagIds = modalityOptions.value.map(option => option.value);
    const useCaseTagIds = useCaseOptions.value.map(option => option.value);
    domain.value = tags.value.filter(tagId => domainTagIds.includes(tagId));
    modality.value = tags.value.filter(tagId => modalityTagIds.includes(tagId));
    useCase.value = tags.value.filter(tagId => useCaseTagIds.includes(tagId));
};

const setPreviewImage = (listingData) => {
    if (listingData.preview_image) {
        previewImage.value = {
            base64: listingData.preview_image,
            name: 'Current Image',
            isUrl: listingData.preview_image.startsWith('http')
        };
    }
};

const combineTags = () => {
    const combined = [];
    
    if (Array.isArray(domain.value) && domain.value.length > 0) {
        combined.push(...domain.value);
    } else if (domain.value) {
        combined.push(domain.value);
    }
    
    if (Array.isArray(modality.value) && modality.value.length > 0) {
        combined.push(...modality.value);
    } else if (modality.value) {
        combined.push(modality.value);
    }
    
    if (Array.isArray(useCase.value) && useCase.value.length > 0) {
        combined.push(...useCase.value);
    } else if (useCase.value) {
        combined.push(useCase.value);
    }
    
    return combined.map(tag => ({ tag_id: parseInt(tag) }));
};

const createListingDetails = () => {
    return {
        name: title.value,
        description: description.value,
        short_description: shortDescription.value,
        delivery_method: deliveryMethod.value,
        consumption_url: 'https://example.com/dataset/123',
        preview_image: previewImage.value?.base64?.startsWith('data:image') ? previewImage.value.base64 : null,
        price: Number(price.value),
        currency: 'USDC',
        data_license: license.value,
        listing_tags: combineTags(),
        receipt_address: '',
        applicant_id: Number(userInfo.value.id),
        expire_at: doesNotExpire.value ? null : (expirationDate.value || null)
    };
};

const prepareListingData = () => {
    const listingDetails = createListingDetails();
    const listingLicense = licenseData.value ? {
        license_id: licenseData.value.licenseId,
        custom_clauses: licenseData.value.customClauses,
        years_duration: licenseData.value.yearsDuration
    } : null;
    
    return listingId.value 
        ? { values: { listing: listingDetails, listing_license: listingLicense } }
        : { listing: listingDetails, listing_license: listingLicense };
};

const handleEditingMode = () => {
    showEditAlert.value = true;
    alertTitle.value = 'Confirm Update';
    alertMessage.value = 'Please enter your password to confirm RFP update.';
    alertType.value = 'warning';
};

const submitCreateListing = async () => {
    if (!validateForm()) return;

    isSubmitting.value = true;
    
    try {
        const listingData = prepareListingData();
        listingDataToSubmit.value = listingData;
        alertShowPasswordInput.value = true;

        if (listingId.value) {
            handleEditingMode();
        } else {
            const response = await createListing(listingData);
            if (response && (response.status === 200 || response.status === 201)) {
                router.push({ path: '/creator-profile', query: { tab: 'rfp' } });
            }
        }
    } finally {
        isSubmitting.value = false;
    }
};


const confirmUpdateListing = async (data) => {
    if (!listingId.value || !listingDataToSubmit.value) return;

    isSubmitting.value = true;
    try {

        const payload = {
            ...listingDataToSubmit.value,
            password: data?.password || null
        };

        const response = await updateListing(listingId.value, payload);

        if (response && (response.status === 200 || response.status === 201)) {
            if (route.name === 'rfp-edit') {
                localStorage.removeItem('temp_editing_listing_id');
            }
            router.push({ path: '/creator-profile', query: { tab: 'rfp' } });
        }
    } catch (error) {
        console.error('Error updating RFP:', error);
    } finally {
        showEditAlert.value = false;
        isSubmitting.value = false;
    }
};

const cancelAlert = () => {
    showAlert.value = false;
    showEditAlert.value = false;
    listingDataToSubmit.value = null;
};

const pageTitle = computed(() => {
    return listingId.value ? 'Edit RFP' : 'Create a new RFP';
});

const buttonText = computed(() => {
    return listingId.value ? 'Update' : 'Create';
});

const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
        if (!file.type.startsWith('image/')) {
            errors.value.previewImage = 'Please select an image file';
            hasUploadError.value = true;
            addToast('Please select a valid image file', 'error');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            imageSource.value = e.target.result;
            showCropper.value = true;
        };
        reader.readAsDataURL(file);
    }
};

const onCropperChange = ({ canvas }) => {
    cropperResult.value = canvas;
};

const saveCroppedImage = () => {
    if (cropperResult.value) {
        const maxSize = 400;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        const ratio = Math.min(maxSize / cropperResult.value.width, maxSize / cropperResult.value.height);
        
        canvas.width = cropperResult.value.width * ratio;
        canvas.height = cropperResult.value.height * ratio;
        
        ctx.drawImage(cropperResult.value, 0, 0, canvas.width, canvas.height);
        
        const base64Image = canvas.toDataURL('image/jpeg', 0.85);
        
        previewImage.value = {
            file: null,
            name: 'cropped-image.jpg',
            base64: base64Image
        };
        
        isUploading.value = false;
        uploadProgress.value = 100;
        hasUploadError.value = false;
        errors.value.previewImage = '';
        showCropper.value = false;
    }
};

const cancelCropping = () => {
    showCropper.value = false;
    const fileInput = document.getElementById('imageInput');
    if (fileInput) fileInput.value = '';
};

const handleBack = () => {
    router.push({ path: '/creator-profile', query: { tab: 'rfp' } });
};



const handlePaste = async (event) => {
    if (event.target?.files?.length > 0) {
        const file = event.target.files[0];
        description.value += `\n\n![Image](Uploading Image...)`;
        await processImageFile(file);
        return;
    }

    if (!event.clipboardData) return;
    
    if (event.clipboardData.types && event.clipboardData.types.includes('text/plain')) {
        return;
    }
    
    const items = event.clipboardData?.items;
    if (!items) return;

    description.value += `\n\n![Image](Uploading Image...)`;

    for (const item of items) {
        if (item.type.startsWith('image/')) {
            event.preventDefault();
            
            const file = item.getAsFile();
            if (file) {
                await processImageFile(file);
            }
            break;
        }
    }
};

const processImageFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
        errors.value.previewImage = 'Please select an image file';
        hasUploadError.value = true;
        addToast('Please select a valid image file', 'error');
        return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
        try {
            const result = await axios.post(`/api/users/${userInfo.value.id}/upload-image`, e.target.result);
            if ((result.status === 200 || result.status === 201) && result.data) {
                imageUrl.value = result.data;
                if (description.value.includes('![Image](Uploading Image...)')) {
                    description.value = description.value.replace('![Image](Uploading Image...)', `![Image](${imageUrl.value})`);
                } else {
                    description.value += `\n\n![Image](${imageUrl.value})`;
                }
                addToast('Image uploaded successfully', 'success');
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            addToast('Failed to upload image', 'error');
        }
    };
    reader.readAsDataURL(file);
};

const handleSaveLicense = (data) => {
    licenseData.value = data;
    showLicenseModal.value = false;
};
</script>

<template>
    <Toast />
    <div v-if="showCropper" class="cropper-modal" data-modal>
        <div class="cropper-container">
            <div class="modal__header">
                <h2 class="modal__title">Crop Your Preview Image</h2>
                <button class="modal__close" @click="cancelCropping">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div class="modal__body">
                <Cropper 
                    ref="cropperRef"
                    :src="imageSource" 
                    @change="onCropperChange" 
                    :stencil-props="{
                        aspectRatio: 1,
                        movable: true,
                        resizable: true,
                        handlers: {
                            eastNorth: true,
                            north: true,
                            westNorth: true,
                            west: true,
                            westSouth: true,
                            south: true,
                            eastSouth: true,
                            east: true
                        },
                        handlersClasses: {
                            default: 'handler-default'
                        },
                        lineWidth: 2
                    }" class="cropper" />
            </div>
            <div class="modal__footer">
                <button class="btn btn--secondary" @click="cancelCropping">Cancel</button>
                <button class="btn btn--primary" @click="saveCroppedImage">Save</button>
            </div>
        </div>
    </div>

    <Alert :show="showEditAlert" :title="alertTitle" :message="alertMessage" :type="alertType"
        :showPasswordInput="alertShowPasswordInput" passwordLabel="Confirm update"
        passwordPlaceholder="Type your password to confirm" :passwordRequired="true" @confirm="confirmUpdateListing"
        @close="cancelAlert" :showCloseBtn="false" />

    <ListingLicenseModal v-model:show="showLicenseModal" @save="handleSaveLicense" @close="showLicenseModal = false" :licenseData="licenseData" />
    <ShowListingLicenseModal v-if="licenseData" v-model:show="showPreviewLicenseModal" @close="showPreviewLicenseModal = false" :licenseData="licenseData" />

    <div class="creator-profile__hero" :class="{ 'creator-profile__hero-edit': listingId }">
        <Navbar />
    </div>

    <div class="creator-profile__container">
        <div v-if="listingId || !showListingForm" class="breadcrumb">
    <router-link to="/creator-profile?tab=rfp" class="breadcrumb-link">
        <svg class="breadcrumb-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd"
                d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                clip-rule="evenodd" />
        </svg>
        <span>Back to RFPs</span>
    </router-link>
</div>
<div v-if="showListingForm && !listingId" class="breadcrumb">
    <button @click="handleBack" class="breadcrumb-link">
        <svg class="breadcrumb-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd"
                d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                clip-rule="evenodd" />
        </svg>
        <span>Back to RFP</span>
    </button>
</div>

        <div class="creator-profile__container-content">
            <h1 class="creator-profile__container-content-title">{{ pageTitle }}</h1>
            <p v-if="!listingId && showListingForm" class="mb-12 text-sm 2xl:text-base text-white/80">
                Now let's create a Request for Proposal (RFP) to request a dataset from the Datalab.
            </p>

            <p v-if="!listingId && !showListingForm" class="mb-12 text-sm 2xl:text-base text-white/80">
                Create a Request for Proposal (RFP) to request a dataset from the Datalab.
            </p>
            
            <div v-else class="listing-form">
                <div class="listing-form-row">
                    <div class="listing-form-field">
                        <div class="field-label-container">
                            <label for="title" class="field-label">Title</label>
                            <InputError :message="errors.title" class="field-error" />
                        </div>
                        <TextInput id="title" v-model="title" placeholder="Enter a title for your RFP"
                            class="w-full" :required="true" :disabled="isSubmitting || isLoading" />
                    </div>
                    <div class="listing-form-field">
                        <div class="field-label-container">
                            <label for="modality" class="field-label">Modality</label>
                            <InputError :message="errors.modality" class="field-error" />
                        </div>
                        <Multiselect v-model="modality" :options="modalityOptions" :multiple="true" :close-on-select="false"
                            :clear-on-select="false" :preserve-search="true" placeholder="Select Modality Tags" class="w-full"
                            @open="() => loadTags(true)" :disabled="isSubmitting || isLoading" />
                    </div>
                </div>
                <div class="listing-form-row">
                    <div class="listing-form-field">
                        <div class="flex gap-4">
                            <div class="w-full">
                                <div class="field-label-container">
                                    <label for="price" class="field-label">Amount<span class="text-gray-400 ml-1">(in USD)</span></label>
                                    <InputError :message="errors.price" class="field-error" />
                                </div>
                                <TextInput 
                                    id="price" 
                                    v-model="price" 
                                    type="number" 
                                    min="0" 
                                    step="0.01" 
                                    placeholder="0.00"
                                    class="w-full" 
                                    :required="true" 
                                    :disabled="isSubmitting || isLoading" 
                                />
                            </div>
                        </div>
                    </div>
                    <div class="listing-form-field flex gap-4">
                        <div class="w-1/2">
                            <div class="field-label-container">
                                <label for="domain" class="field-label">Domain<span class="text-white/80 ml-1">(Optional)</span></label>
                            </div>
                            <Multiselect v-model="domain" :options="domainOptions" :multiple="true" :close-on-select="false"
                                :clear-on-select="false" :preserve-search="true" placeholder="Select Domain Tags" class="w-full"
                                @open="() => loadTags(true)" :disabled="isSubmitting || isLoading" />
                        </div>
                        <div class="w-1/2">
                            <div class="field-label-container">
                                <label for="useCase" class="field-label">Use Case<span class="text-white/80 ml-1">(Optional)</span></label>
                            </div>
                            <Multiselect v-model="useCase" :options="useCaseOptions" :multiple="true" :close-on-select="false"
                                :clear-on-select="false" :preserve-search="true" placeholder="Select Use Case Tags" class="w-full"
                                @open="() => loadTags(true)" :disabled="isSubmitting || isLoading" />
                        </div>
                    </div>
                </div>
                <div class="listing-form-row">
                    <div class="flex flex-col gap-4">
                        <div class="listing-form-field flex gap-4">
                            <div class="flex flex-col w-full">
                                <div class="field-label-container">
                                    <label for="license" class="field-label">License</label>
                                    <a href="https://docs.portexai.com/portex-docs/core-concepts/psdla" target="_blank"
                                        class="text-white/80 hover:text-echoes-primary transition-colors relative group">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="info-circle"
                                            class="w-4 h-4">
                                            <path fill="currentColor"
                                                d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z">
                                            </path>
                                        </svg>
                                        <span class="tooltip">Learn more about PSDLA license</span>
                                    </a>
                                    <InputError :message="errors.license" class="field-error" />
                                </div>
                                <button @click="showLicenseModal = true" v-if="!licenseData"
                                    class="text-sm w-1/2 border border-echoes-border-gray text-white rounded-xl py-2 px-4 transition-colors hover:border-white/20 items-center flex justify-center gap-6">
                                    Configure License
                                    <i class="uil uil-arrow-circle-right text-lg"></i>
                                </button>
                                <div v-else class="text-sm w-full border border-echoes-border-gray text-white rounded-xl py-2 px-4 transition-colors hover:border-white/20 items-center flex justify-between">
                                    <div>
                                        {{ licenseData.licenseName }}
                                    </div>
                                    <div class="flex gap-2">
                                        <button class="text-sm bg-white/20 rounded-md px-2 py-1" @click="showPreviewLicenseModal = true">Preview</button>
                                        <button class="text-sm bg-white/20 rounded-md px-2 py-1" @click="showLicenseModal = true">Edit</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col mt-1.5">
                                <div class="field-label-container gap-6">
                                    <label for="expiration" class="field-label">Expiration Date<span class="text-white/80 ml-1">(Optional)</span></label>
                                    
                                    <InputError :message="errors.expiration" class="field-error" />
                                </div>
                                <div class="flex gap-8">
                                    <input
                                        type="date"
                                        id="expiration"
                                        v-model="expirationDate"
                                        class="w-full px-4 py-2.5 text-sm rounded-[13px] bg-echoes-background-black text-white border border-echoes-border-gray transition-all duration-200 ease-in-out focus:outline-none  focus:border-echoes-primary"
                                        :class="{ 'input-disabled': doesNotExpire }"
                                        :disabled="doesNotExpire || isSubmitting || isLoading"
                                        :min="new Date().toISOString().split('T')[0]"
                                    />
                                    <div class="flex items-center gap-2 cursor-pointer select-none w-1/2">
                                        <Checkbox v-model="doesNotExpire" />
                                        <span class="text-sm text-gray-200">Does not expire</span>
                                    </div>
                                </div>
                            </div>
                        <div class="listing-form-field mt-1">
                            <div class="field-label-container">
                                <label for="previewImage" class="field-label">Preview Image</label>
                                <InputError :message="errors.previewImage" class="field-error" />
                            </div>
                            <div class="preview-image-container">
                                <div class="image-upload-controls">
                                    <button @click="$refs.imageInput.click()" class="upload-btn"
                                        :class="{ 'upload-btn-selected': previewImage }"
                                        :disabled="isSubmitting || isLoading">
                                        <svg class="w-6 h-6" :class="previewImage ? 'text-green-400' : 'text-white/80'"
                                            viewBox="0 0 94 94" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M15.6667 58.7501L15.6667 66.5834C15.6667 73.0728 20.9274 78.3334 27.4167 78.3334L66.5834 78.3334C73.0728 78.3334 78.3334 73.0728 78.3334 66.5834V58.7501M47.0001 15.6667L47.0001 62.6667M47.0001 15.6667L62.6667 31.3334M47.0001 15.6667L31.3334 31.3334"
                                                stroke="currentColor" stroke-width="6" stroke-linecap="round"
                                                stroke-linejoin="round" />
                                        </svg>
                                        <span v-if="!previewImage">Upload</span>
                                        <span v-else class="flex items-center">
                                            <span class="truncate max-w-[120px] inline-block overflow-hidden">{{
                                                previewImage.name }}</span>
                                            <button @click.stop="removeSelectedImage" class="ml-2 p-1 rounded-full"
                                                title="Remove image">
                                                <svg class="w-2.5 h-2.5 text-white/80 hover:text-red-500 transition-all duration-200 ease-in-out"
                                                    aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none"
                                                    viewBox="0 0 14 14">
                                                    <path stroke="currentColor" stroke-linecap="round"
                                                        stroke-linejoin="round" stroke-width="2"
                                                        d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                                                </svg>
                                            </button>
                                        </span>
                                    </button>
                                    <input ref="imageInput" type="file" id="imageInput" @change="handleImageUpload"
                                        class="hidden" accept="image/*" />
                                    <span class="upload-or">or</span>
                                    <button @click="generatePreviewImage" class="generate-btn" 
                                        :disabled="isSubmitting || isLoading || true">
                                        <svg class="w-6 h-6 text-white/80" aria-hidden="true"
                                            xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                            viewBox="0 0 24 24">
                                            <path stroke="currentColor" stroke-linecap="round" stroke-width="2"
                                                d="M8.737 8.737a21.49 21.49 0 0 1 3.308-2.724m0 0c3.063-2.026 5.99-2.641 7.331-1.3 1.827 1.828.026 6.591-4.023 10.64-4.049 4.049-8.812 5.85-10.64 4.023-1.33-1.33-.736-4.218 1.249-7.253m6.083-6.11c-3.063-2.026-5.99-2.641-7.331-1.3-1.827 1.828-.026 6.591 4.023 10.64m3.308-9.34a21.497 21.497 0 0 1 3.308 2.724m2.775 3.386c1.985 3.035 2.579 5.923 1.248 7.253-1.336 1.337-4.245.732-7.295-1.275M14 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z" />
                                        </svg>
                                        Generate
                                        <span class="tooltip">Coming soon</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="listing-form-field">
                        <div class="field-label-container">
                            <label for="shortDescription" class="field-label">Short Description</label>
                            <InputError :message="errors.shortDescription" class="field-error" />
                            <span class="character-counter">{{ shortDescription.length }}/300</span>
                        </div>
           
                            <textarea class="text-area__field h-[230px]" v-model="shortDescription"
                            placeholder="Add a short description for your RFP (max 300 characters)"
                            maxlength="300" />
                    </div>
                </div>
                <div class="listing-form-field w-full">
                    <div class="field-label-container">
                        <label for="description" class="field-label">Description</label>
                        <InputError :message="errors.description" class="field-error" />
                    </div>
                        <MarkdownRenderer v-model="description"
                            placeholder="Use markdown to format your description"
                            :is-editing="listingId" :disabled="isSubmitting || isLoading" 
                            @paste="handlePaste"
                        />
                       
                </div>
                <div class="action-container">
                    <button class="create-listing-btn" @click="submitCreateListing" :disabled="isSubmitting || isLoading || isFileProcessing">
                        <ButtonLoading v-if="isSubmitting" :text="listingId ? 'Updating...' : 'Creating...'"
                            size="sm" />
                        <span v-else class="text-sm">{{ buttonText }}</span>
                        <span v-if="isFileProcessing" class="tooltip">Waiting for file processing to complete...</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="postcss">
.creator-profile__hero {
    @apply relative;
    background-size: 100% 100%;
    background-position: 0px 0px;
    background-repeat: no-repeat;
}

.creator-profile__hero-edit {
    @apply relative;
    background-size: 100% 100%;
    background-position: 0px 0px;
    background-repeat: no-repeat;
}



.creator-profile__container {
    @apply relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-20;
  }

.creator-profile__container-stepper {
    @apply flex flex-col items-center justify-center p-8 2xl:p-12;
}

.creator-profile__container-content {
    @apply mx-auto max-w-5xl 2xl:max-w-7xl;
}

.creator-profile__container-content-title {
    @apply text-2xl 2xl:text-3xl font-bold mb-3 bg-gradient-to-r from-[#FFFFFF] to-[#999999] bg-clip-text text-transparent w-fit;
}

.listing-form {
    @apply space-y-6;
}

.listing-form-row {
    @apply grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6;
}

.listing-form-field {
    @apply mb-2 md:mb-0;
}

.field-label-container {
    @apply flex items-center mb-2 w-fit gap-2;
}

.field-label {
    @apply text-sm font-medium text-white/80;
}


.field-error {
    @apply text-xs text-red-500 m-0;
}

.textarea-container {
    @apply relative h-full;
}

.character-counter {
    @apply text-xs text-white/80 ml-auto mr-1;
}



.meta-divider {
    @apply text-xs font-bold text-white/80 mx-1;
}


.schema-actions {
    @apply flex gap-2 items-center w-full sm:w-auto;
}

.preview-button,
.edit-button {
    @apply px-5 py-1.5 text-white rounded-md text-sm transition-colors flex-1 sm:flex-initial;

    &:disabled {
        @apply opacity-50 cursor-not-allowed;
    }
}

.preview-button {
    @apply bg-echoes-background-black hover:bg-echoes-background-black/50 py-1.5 px-3 text-xs lg:py-1.5 lg:px-3.5 lg:text-sm;

    &:disabled {
        @apply hover:bg-echoes-background-black/50;
    }
}

.edit-button {
    @apply bg-echoes-background-black hover:bg-echoes-background-black/50 py-1.5 px-3 text-xs lg:py-1.5 lg:px-3.5 lg:text-sm;

    &:disabled {
        @apply hover:bg-echoes-background-black/50;
    }
}

.preview-image-container {
    @apply flex;
}

.image-upload-controls {
    @apply flex items-center gap-2 w-full;
}

.upload-btn,
.generate-btn {
    @apply w-full sm:flex-1 h-11 flex items-center gap-2 justify-center px-3 sm:px-5 py-2 text-sm rounded-2xl bg-echoes-background-black text-white border border-echoes-border-gray placeholder-white/80 hover:border-echoes-primary hover:bg-echoes-background-gray/50 hover:text-white transition-all duration-200 ease-in-out;

    &:disabled {
        @apply opacity-50 cursor-not-allowed hover:bg-echoes-background-black/50 hover:border-echoes-border-gray;
    }
}

.upload-btn {
    @apply bg-echoes-background-black text-white hover:bg-echoes-background-black/50;

    &:disabled {
        @apply hover:bg-echoes-background-black/50;
    }
}

.upload-btn-selected {
    @apply border border-green-500 bg-echoes-background-black dark:border-green-400;

    &:disabled {
        @apply opacity-50 cursor-not-allowed;
    }
}

.action-container {
    @apply flex items-center justify-center md:justify-end mt-8;
}

.upload-or {
    @apply text-gray-400 text-sm my-1 sm:my-0;
}

.generate-btn {
    @apply bg-echoes-background-black text-white;
    position: relative;
}

.generate-btn:disabled {
    @apply opacity-60 cursor-not-allowed;
}

.tooltip {
    @apply absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 text-xs font-semibold text-white bg-echoes-background-gray rounded-lg whitespace-normal w-max max-w-[350px];
    visibility: hidden;
    opacity: 0;
    transition: all 0.2s ease-in-out;
    z-index: 50;
}

a:hover .tooltip,
button:hover .tooltip {
    visibility: visible;
    opacity: 1;
}

.image-btn-listing {
    @apply w-8 h-8 bg-gradient-to-br from-green-500 to-green-700 text-white font-semibold rounded-md hover:bg-gradient-to-br hover:from-green-600 hover:to-green-800 transition duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed disabled:from-green-500/60 disabled:to-green-700/60;
}

.create-listing-btn {
    @apply px-6 2xl:px-8 py-2 mb-6 text-sm 2xl:text-base bg-gradient-to-br from-green-500 to-green-700 text-white font-semibold rounded-full hover:bg-gradient-to-br hover:from-green-600 hover:to-green-800 transition duration-200 flex items-center justify-center min-w-[160px] w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed;
    position: relative;
}

.schema-empty-state {
    @apply flex items-center justify-center w-full h-full py-3;
}

.schema-empty-message {
    @apply text-gray-400 text-sm;
}

.breadcrumb {
    @apply my-6 w-fit;
}

.breadcrumb-link {
    @apply flex underline items-center gap-1 text-white/80 hover:text-echoes-primary transition-colors duration-200 text-sm;
}

.breadcrumb-icon {
    @apply w-4 h-4;
}

.cropper-modal {
    @apply fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2;
    backdrop-filter: blur(4px);
    animation: fadeIn 0.2s ease;
    isolation: isolate;
}

.cropper-container {
    @apply overflow-hidden bg-echoes-background-black rounded-xl shadow-xl w-full max-h-[85vh] mt-10 flex flex-col;
    animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    z-index: 1;
    max-width: 540px;
}

.modal__header {
    @apply sticky top-0 z-10 flex items-center justify-between p-4 border-b border-echoes-border-gray bg-echoes-background-black;
}

.modal__title {
    @apply text-xl font-semibold text-white;
}

.modal__close {
    @apply text-white/80 hover:text-echoes-primary p-1 rounded-full transition-colors;
}

.modal__body {
    @apply p-4 overflow-y-auto;
    flex: 1;
}

.modal__footer {
    @apply sticky bottom-0 z-10 p-4 border-t border-echoes-border-gray flex items-center justify-end gap-3 bg-echoes-background-black;
}

.btn {
    @apply px-4 py-2 rounded-lg font-medium transition-colors;
}

.btn--secondary {
    @apply px-4 py-1.5 rounded-lg text-sm font-medium transition-colors hover:border-echoes-primary/50 hover:text-echoes-primary bg-echoes-background-black text-white border border-echoes-border-gray;
}

.btn--primary {
    @apply px-4 py-1.5 rounded-lg text-sm font-medium transition-colors bg-echoes-primary text-white border-none hover:bg-echoes-primary/50;
}

.cropper {
    @apply bg-zinc-900;
}

.handler-default {
    @apply w-3 h-3 bg-echoes-primary border border-white rounded-full;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.text-area__field {
    @apply w-full h-full px-4 py-3 text-sm bg-echoes-background-black text-white border border-echoes-border-gray rounded-[13px] placeholder-white/70 focus:outline-none  focus:border-echoes-primary resize-none transition-all duration-200;

    &::placeholder {
        @apply transition-all duration-200 text-white/50 hover:text-white/80;
    }

    &:hover {
        @apply bg-echoes-background-black
        border-white/20
        text-white
        ;
    }

    &:focus::placeholder {
        @apply opacity-0 translate-x-1 border-echoes-primary;
    }

    &:focus {
        @apply border-echoes-primary;
    }

    &:disabled {
        @apply cursor-not-allowed opacity-60 bg-echoes-background-gray/30;
    }
}

.back-button {
    @apply flex items-center gap-2 text-white/80 hover:text-echoes-primary transition-colors text-sm;
}

.input-disabled {
    @apply opacity-60 cursor-not-allowed bg-echoes-background-black text-white/80;
}

/* Adicionando estilo para o ícone do calendário */
input[type="date"]::-webkit-calendar-picker-indicator {
    filter: invert(100%) sepia(0%) saturate(0%) hue-rotate(288deg) brightness(100%) contrast(100%);
}
</style>
